import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Lightbulb, Search, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useFoodFacts } from '@/hooks/useFoodFacts';
import { MealCategory } from '@/types';

const FoodFacts = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('TODOS');
  const [searchQuery, setSearchQuery] = useState('');

  const { foodFacts, loading, error } = useFoodFacts(
    selectedCategory === 'TODOS' ? undefined : selectedCategory,
    searchQuery
  );

  // Categorías disponibles (basadas en el backend)
  const categories = [
    'TODOS',
    MealCategory.DESAYUNO,
    MealCategory.COMIDA,
    MealCategory.CENA,
    MealCategory.SNACK,
    'GRASAS_SALUDABLES',
    'ANTIOXIDANTES',
    'PROTEINAS',
    'FIBRA',
    'OMEGA3',
    'MICRONUTRIENTES'
  ];

  // Mapeo de categorías del backend a nombres amigables
  const getCategoryDisplayName = (category: string) => {
    const categoryMap: { [key: string]: string; } = {
      [MealCategory.DESAYUNO]: 'Desayuno',
      [MealCategory.COMIDA]: 'Comida',
      [MealCategory.CENA]: 'Cena',
      [MealCategory.SNACK]: 'Snack',
      'GRASAS_SALUDABLES': 'Grasas Saludables',
      'ANTIOXIDANTES': 'Antioxidantes',
      'PROTEINAS': 'Proteínas',
      'FIBRA': 'Fibra y Control Glucémico',
      'OMEGA3': 'Omega-3 y Fibra',
      'MICRONUTRIENTES': 'Micronutrientes',
      'TODOS': 'Todos'
    };
    return categoryMap[category] || category;
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string; } = {
      [MealCategory.DESAYUNO]: "bg-blue-100 text-blue-800 border-blue-200",
      [MealCategory.COMIDA]: "bg-green-100 text-green-800 border-green-200",
      [MealCategory.CENA]: "bg-purple-100 text-purple-800 border-purple-200",
      [MealCategory.SNACK]: "bg-orange-100 text-orange-800 border-orange-200",
      "GRASAS_SALUDABLES": "bg-emerald-100 text-emerald-800 border-emerald-200",
      "ANTIOXIDANTES": "bg-pink-100 text-pink-800 border-pink-200",
      "PROTEINAS": "bg-red-100 text-red-800 border-red-200",
      "FIBRA": "bg-teal-100 text-teal-800 border-teal-200",
      "OMEGA3": "bg-indigo-100 text-indigo-800 border-indigo-200",
      "MICRONUTRIENTES": "bg-amber-100 text-amber-800 border-amber-200"
    };
    return colors[category] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  // Generar detalles enriquecidos basados en keywords
  const getEnrichedDetails = (foodFact: any) => {
    if (foodFact.keywords && foodFact.keywords.length > 0) {
      const keywordDetails: { [key: string]: string; } = {
        'aguacate': 'El aguacate mexicano contiene ácido oleico, el mismo tipo de grasa monoinsaturada que hace famoso al aceite de oliva, pero en mayor concentración. Además, mejora la absorción de carotenoides de otros vegetales hasta en 15 veces.',
        'chile': 'La capsaicina en los chiles no solo da el picor, sino que también acelera el metabolismo hasta en un 25% durante 3 horas después de comer. Los chiles también son ricos en capsantina, un antioxidante que protege contra el cáncer.',
        'frijol': 'Los frijoles negros mexicanos contienen 21g de proteína por taza cocida y son ricos en folato, magnesio y fibra. Esta combinación milenaria era la base nutricional de las civilizaciones mesoamericanas.',
        'nopal': 'Una taza de nopal crudo tiene solo 14 calorías pero 2g de fibra soluble que ayuda a controlar el azúcar en sangre. Los estudios muestran que comer nopal antes de las comidas puede reducir los picos de glucosa en un 48%.',
        'cacao': 'El cacao original de México contiene teobromina, que mejora el flujo sanguíneo al cerebro, y flavonoides que pueden reducir la presión arterial. Los aztecas lo consideraban la bebida de los dioses por sus propiedades energéticas y mentales.',
        'chia': 'Una cucharada de chía (15g) proporciona 5g de fibra, 3g de proteína y 2g de omega-3. Al contacto con líquido, forma un gel que ralentiza la digestión y ayuda a mantener estables los niveles de energía durante horas.',
        'amaranto': 'El amaranto era tan valorado por los aztecas que lo usaban en ceremonias religiosas. Contiene lisina, aminoácido escaso en otros cereales, y escualeno, compuesto que ayuda a reducir el colesterol. Es más nutritivo que la quinoa y el arroz.',
        'quelites': 'Las verdolagas, por ejemplo, son la fuente vegetal más rica en omega-3 y contienen más betacaroteno que las zanahorias. Los quelites fueron la base de la alimentación prehispánica y son extremadamente resistentes y nutritivos.'
      };

      for (const keyword of foodFact.keywords) {
        if (keywordDetails[keyword]) {
          return keywordDetails[keyword];
        }
      }
    }

    // Detalles por defecto basados en la categoría
    const defaultDetails: { [key: string]: string; } = {
      [MealCategory.DESAYUNO]: 'Un desayuno balanceado es fundamental para iniciar el día con energía y mantener un rendimiento cognitivo óptimo durante toda la mañana.',
      [MealCategory.COMIDA]: 'La comida principal del día debe proporcionar nutrientes esenciales para mantener la energía y funciones corporales durante la tarde.',
      [MealCategory.CENA]: 'Una cena ligera pero nutritiva favorece la digestión, mejora la calidad del sueño y permite una adecuada recuperación nocturna.',
      [MealCategory.SNACK]: 'Los snacks inteligentes entre comidas ayudan a mantener el metabolismo activo y previenen los atracones en las comidas principales.'
    };

    return defaultDetails[foodFact.category] || 'Este alimento contiene nutrientes esenciales que contribuyen a una alimentación balanceada y saludable.';
  };

  // Generar consejo saludable basado en keywords y categoría
  const getHealthTip = (foodFact: any) => {
    const keywordTips: { [key: string]: string; } = {
      'aguacate': 'Agrega aguacate a tus ensaladas para absorber mejor las vitaminas A, D, E y K de las verduras.',
      'chile': 'Incluye chiles frescos en tu dieta diaria para aumentar tu metabolismo naturalmente.',
      'frijol': 'Come frijoles con tortillas de maíz para obtener proteína completa sin necesidad de carne.',
      'nopal': 'Come ensalada de nopales antes de comidas abundantes para controlar mejor el azúcar en sangre.',
      'cacao': 'Elige chocolate con al menos 70% de cacao mexicano para obtener máximos beneficios antioxidantes.',
      'chia': 'Remoja las semillas de chía 15 minutos antes de consumir para mejor digestión y absorción.',
      'amaranto': 'Sustituye el arroz por amaranto cocido para obtener más proteína y nutrientes.',
      'quelites': 'Incluye quelites en ensaladas, sopas y guisos para aumentar dramáticamente el valor nutricional.'
    };

    for (const keyword of foodFact.keywords || []) {
      if (keywordTips[keyword]) {
        return keywordTips[keyword];
      }
    }

    // Consejos por defecto por categoría
    const defaultTips: { [key: string]: string; } = {
      [MealCategory.DESAYUNO]: 'Incluye proteínas y fibra en tu desayuno para mantenerte satisfecho hasta la comida.',
      [MealCategory.COMIDA]: 'Combina proteínas magras con vegetales de colores variados para una comida balanceada.',
      [MealCategory.CENA]: 'Cena al menos 2 horas antes de dormir para una mejor digestión y calidad de sueño.',
      [MealCategory.SNACK]: 'Elige snacks con proteína y fibra para mantenerte satisfecho entre comidas.'
    };

    return defaultTips[foodFact.category] || 'Incorpora este alimento regularmente en tu dieta para obtener sus beneficios nutricionales.';
  };
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <Lightbulb className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Datos Nutricionales</h1>
                  <p className="text-gray-600 text-sm">Cargando información...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <Card key={n} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-full"></div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <Lightbulb className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Datos Nutricionales</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">Error: {error}</div>
            <Button onClick={() => window.location.reload()}>
              Reintentar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-full">
                <Lightbulb className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Datos Nutricionales</h1>
                <p className="text-gray-600 text-sm">
                  {foodFacts.length} datos nutricionales basados en ciencia
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {foodFacts.length === 0 ? (
          <div className="text-center py-12">
            <Lightbulb className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No se encontraron datos nutricionales
            </h3>
            <p className="text-gray-600 mb-4">
              No hay datos nutricionales disponibles en este momento.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6 text-sm text-gray-600">
              Mostrando {foodFacts.length} dato{foodFacts.length !== 1 ? 's' : ''} nutricional{foodFacts.length !== 1 ? 'es' : ''}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {foodFacts.map((fact) => (
                <Card key={fact.id} className="h-fit hover:shadow-lg transition-shadow duration-300">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between mb-2">
                      <Badge
                        variant="outline"
                        className={getCategoryColor(fact.category || 'general')}
                      >
                        {getCategoryDisplayName(fact.category || 'general')}
                      </Badge>
                      {fact.relatedRecipesCount && fact.relatedRecipesCount > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {fact.relatedRecipesCount} receta{fact.relatedRecipesCount !== 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg font-semibold text-gray-900 leading-tight">
                      {fact.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-1 text-sm">Dato Científico</h4>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {fact.fact}
                        </p>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-100">
                      <a
                        href={fact.sourceUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 text-xs transition-colors ${fact.sourceUrl
                          ? 'text-primary hover:text-primary/80'
                          : 'text-gray-500 cursor-not-allowed'
                          }`}
                      >
                        <span>Fuente: {fact.source}</span>
                        {fact.sourceUrl && <ExternalLink className="h-3 w-3" />}
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
  // if (loading) {
  //   return (
  //     <div className="min-h-screen bg-gray-50">
  //       <div className="bg-white border-b shadow-sm">
  //         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
  //           <div className="flex items-center gap-4">
  //             <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
  //               <ArrowLeft className="h-4 w-4 mr-2" />
  //               Volver
  //             </Button>
  //             <div className="flex items-center gap-3">
  //               <div className="p-2 bg-amber-100 rounded-full">
  //                 <Lightbulb className="h-5 w-5 text-amber-600" />
  //               </div>
  //               <div>
  //                 <h1 className="text-2xl font-bold text-gray-900">Datos Nutricionales</h1>
  //                 <p className="text-gray-600 text-sm">Cargando información...</p>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
  //         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
  //           {[1, 2, 3, 4, 5, 6].map((n) => (
  //             <Card key={n} className="animate-pulse">
  //               <CardHeader>
  //                 <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
  //                 <div className="h-6 bg-gray-200 rounded w-full"></div>
  //               </CardHeader>
  //               <CardContent className="space-y-3">
  //                 <div className="h-4 bg-gray-200 rounded w-full"></div>
  //                 <div className="h-4 bg-gray-200 rounded w-2/3"></div>
  //                 <div className="h-4 bg-gray-200 rounded w-3/4"></div>
  //               </CardContent>
  //             </Card>
  //           ))}
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // if (error) {
  //   return (
  //     <div className="min-h-screen bg-gray-50">
  //       <div className="bg-white border-b shadow-sm">
  //         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
  //           <div className="flex items-center gap-4">
  //             <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
  //               <ArrowLeft className="h-4 w-4 mr-2" />
  //               Volver
  //             </Button>
  //             <div className="flex items-center gap-3">
  //               <div className="p-2 bg-amber-100 rounded-full">
  //                 <Lightbulb className="h-5 w-5 text-amber-600" />
  //               </div>
  //               <div>
  //                 <h1 className="text-2xl font-bold text-gray-900">Datos Nutricionales</h1>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
  //         <div className="text-center py-12">
  //           <div className="text-red-600 mb-4">Error: {error}</div>
  //           <Button onClick={() => window.location.reload()}>
  //             Reintentar
  //           </Button>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // return (
  //   <div className="min-h-screen bg-gray-50">
  //     {/* Header */}
  //     <div className="bg-white border-b shadow-sm">
  //       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
  //         <div className="flex items-center gap-4">
  //           <Button
  //             variant="ghost"
  //             size="sm"
  //             onClick={() => navigate(-1)}
  //             className="flex items-center gap-2"
  //           >
  //             <ArrowLeft className="h-4 w-4" />
  //             Volver
  //           </Button>
  //           <div className="flex items-center gap-3">
  //             <div className="p-2 bg-amber-100 rounded-full">
  //               <Lightbulb className="h-5 w-5 text-amber-600" />
  //             </div>
  //             <div>
  //               <h1 className="text-2xl font-bold text-gray-900">Datos Nutricionales</h1>
  //               <p className="text-gray-600 text-sm">
  //                 {foodFacts.length} datos nutricionales basados en ciencia
  //               </p>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>

  //     {/* Filtros y Búsqueda */}
  //     <div className="bg-white border-b">
  //       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
  //         <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
  //           <div className="flex items-center gap-2">
  //             <Filter className="h-4 w-4 text-gray-500" />
  //             <span className="text-sm font-medium text-gray-700">Filtrar por:</span>
  //             <div className="flex flex-wrap gap-2">
  //               {categories.map((category) => (
  //                 <Button
  //                   key={category}
  //                   variant={selectedCategory === category ? "default" : "outline"}
  //                   size="sm"
  //                   onClick={() => setSelectedCategory(category)}
  //                   className="text-xs"
  //                 >
  //                   {getCategoryDisplayName(category)}
  //                 </Button>
  //               ))}
  //             </div>
  //           </div>

  //           <div className="relative w-full sm:w-64">
  //             <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
  //             <Input
  //               placeholder="Buscar datos nutricionales..."
  //               value={searchQuery}
  //               onChange={(e) => setSearchQuery(e.target.value)}
  //               className="pl-10 pr-4"
  //             />
  //           </div>
  //         </div>
  //       </div>
  //     </div>

  //     {/* Content */}
  //     <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
  //       {foodFacts.length === 0 ? (
  //         <div className="text-center py-12">
  //           <Lightbulb className="h-12 w-12 text-gray-300 mx-auto mb-4" />
  //           <h3 className="text-lg font-semibold text-gray-900 mb-2">
  //             No se encontraron datos nutricionales
  //           </h3>
  //           <p className="text-gray-600 mb-4">
  //             {searchQuery
  //               ? `No hay resultados para "${searchQuery}". Intenta con otros términos.`
  //               : 'No hay datos nutricionales disponibles en esta categoría.'
  //             }
  //           </p>
  //           {(searchQuery || selectedCategory !== 'TODOS') && (
  //             <Button
  //               onClick={() => {
  //                 setSearchQuery('');
  //                 setSelectedCategory('TODOS');
  //               }}
  //             >
  //               Ver todos los datos
  //             </Button>
  //           )}
  //         </div>
  //       ) : (
  //         <>
  //           <div className="mb-6 text-sm text-gray-600">
  //             Mostrando {foodFacts.length} dato{foodFacts.length !== 1 ? 's' : ''} nutricional{foodFacts.length !== 1 ? 'es' : ''}
  //             {selectedCategory !== 'TODOS' && ` en ${getCategoryDisplayName(selectedCategory)}`}
  //             {searchQuery && ` para "${searchQuery}"`}
  //           </div>

  //           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
  //             {foodFacts.map((fact) => (
  //               <Card key={fact.id} className="h-fit hover:shadow-lg transition-shadow duration-300">
  //                 <CardHeader className="pb-4">
  //                   <div className="flex items-start justify-between mb-2">
  //                     <Badge
  //                       variant="outline"
  //                       className={getCategoryColor(fact.category || 'general')}
  //                     >
  //                       {getCategoryDisplayName(fact.category || 'general')}
  //                     </Badge>
  //                     {fact.relatedRecipesCount && fact.relatedRecipesCount > 0 && (
  //                       <Badge variant="secondary" className="text-xs">
  //                         {fact.relatedRecipesCount} receta{fact.relatedRecipesCount !== 1 ? 's' : ''}
  //                       </Badge>
  //                     )}
  //                   </div>
  //                   <CardTitle className="text-lg font-semibold text-gray-900 leading-tight">
  //                     {fact.title}
  //                   </CardTitle>
  //                 </CardHeader>

  //                 <CardContent className="space-y-4">
  //                   <div className="space-y-3">
  //                     <div>
  //                       <h4 className="font-medium text-gray-900 mb-1 text-sm">Dato Científico</h4>
  //                       <p className="text-gray-700 text-sm leading-relaxed">
  //                         {fact.fact}
  //                       </p>
  //                     </div>

  //                     {/* <div>
  //                       <h4 className="font-medium text-gray-900 mb-1 text-sm">Por Qué Importa</h4>
  //                       <p className="text-gray-600 text-sm leading-relaxed">
  //                         {getEnrichedDetails(fact)}
  //                       </p>
  //                     </div>

  //                     <div className="bg-green-50 p-3 rounded-md border border-green-200">
  //                       <h4 className="font-medium text-green-800 mb-1 text-sm">💡 Consejo Saludable</h4>
  //                       <p className="text-green-700 text-sm">
  //                         {getHealthTip(fact)}
  //                       </p>
  //                     </div> */}
  //                   </div>

  //                   <div className="pt-3 border-t border-gray-100">
  //                     <a
  //                       href={fact.sourceUrl || '#'}
  //                       target="_blank"
  //                       rel="noopener noreferrer"
  //                       className={`flex items-center gap-2 text-xs transition-colors ${fact.sourceUrl
  //                         ? 'text-primary hover:text-primary/80'
  //                         : 'text-gray-500 cursor-not-allowed'
  //                         }`}
  //                     >
  //                       <span>Fuente: {fact.source}</span>
  //                       {fact.sourceUrl && <ExternalLink className="h-3 w-3" />}
  //                     </a>
  //                   </div>
  //                 </CardContent>
  //               </Card>
  //             ))}
  //           </div>
  //         </>
  //       )}
  //     </div>
  //   </div>
  // );
};

export default FoodFacts;